import { THREAD_FORM_SHIFT } from '@components/ArtBoard/Threads/constants'
import type { NewThreadDraft } from '@components/ArtBoard/Threads/types'
import {
  findCommentableElement,
  getCommentableRect,
  getEventOffset,
  getLocalPosition,
  getViewportPosition,
} from '@components/ArtBoard/Threads/utils'
import { artBoardActions } from '@store/reducers/artboard'
import {
  type MouseEventHandler,
  type PointerEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'

import { useScaleZoom } from '@/hooks'
import type { Offset } from '@/utils/offset'

// a press that travelled further than this is a pan, not a click
const CLICK_TOLERANCE = 3

const isSameSpot = (a: Offset | null, b: Offset) =>
  !!a &&
  Math.abs(a.x - b.x) <= CLICK_TOLERANCE &&
  Math.abs(a.y - b.y) <= CLICK_TOLERANCE

const getDraftPosition = (draft: NewThreadDraft, scale: number) => {
  const el = findCommentableElement(draft.commentable)

  if (!el) {
    return null
  }

  return getViewportPosition(
    draft.offset,
    el.getBoundingClientRect(),
    scale,
    THREAD_FORM_SHIFT
  )
}

// The draft is kept in the coordinates of its commentable element, so the
// form follows the canvas: its on-screen position is recomputed on every
// canvas move and zoom (the `canvas` window event, see buildCanvasEvent).
export const useNewThreadPosition = () => {
  const dispatch = useDispatch()
  const { scale } = useScaleZoom()

  const [draft, setDraft] = useState<NewThreadDraft | null>(null)
  const [position, setPosition] = useState<Offset | null>(null)
  const pressRef = useRef<Offset | null>(null)

  useEffect(() => {
    if (!draft) {
      return
    }

    const follow = (e: Event) => {
      const { scale: next } = (e as CustomEvent<{ scale: number }>).detail
      const nextPosition = getDraftPosition(draft, next)

      // the element is gone (another tab, a deleted node): nothing to attach to
      if (!nextPosition) {
        setDraft(null)
      }

      setPosition(nextPosition)
    }

    window.addEventListener('canvas', follow)

    return () => {
      window.removeEventListener('canvas', follow)
    }
  }, [draft])

  const onPointerDown: PointerEventHandler = (e) => {
    pressRef.current = getEventOffset(e)
  }

  const onClick: MouseEventHandler = (e) => {
    const point = getEventOffset(e)

    if (!isSameSpot(pressRef.current, point)) {
      return
    }

    if (!draft) {
      const treeEl = document.getElementById('tree') as HTMLElement
      const [commentable, rect] = getCommentableRect(point, treeEl)

      setDraft({
        commentable,
        offset: getLocalPosition(point, rect, scale, THREAD_FORM_SHIFT),
      })
      setPosition(point)
      return
    }

    setDraft(null)
    setPosition(null)
    dispatch(artBoardActions.changeMode('edit'))
  }

  const onReset = () => {
    setDraft(null)
    setPosition(null)
  }

  return { draft, position, onPointerDown, onClick, onReset }
}
